// MentionPopover.tsx
import React from 'react';
import GlyphAtom from '../Glyph/GlyphAtom';
import './MentionAtom.scss';
export type SearchUserResult = {
  id: string;
  type?: string;
  name?: string;
  email?: string;
  imageId?: string;
  avatar?: string;
};

export type MentionPopoverPick = SearchUserResult | { custom: true; email: string; display: string };

export type MentionPopoverProps = {
  open: boolean;
  anchor: { left: number; top: number } | null;
  loading: boolean;
  results: SearchUserResult[];
  activeIndex: number;
  query: string;
  onHover: (index: number) => void;
  onPick: (item: MentionPopoverPick) => void;
};

const isEmail = (s: string) => /^[^\s@]+@[^\s@]{1,256}\.[^\s@]{2,}$/.test(s);

const MentionPopover: React.FC<MentionPopoverProps> = ({ open, anchor, loading, results, activeIndex, query, onHover, onPick }) => {
  if (!open || !anchor) return null;

  const showQuickEmail = !!query && isEmail(query) && !results.length && !loading;

  const handleKeyDown = (event: React.KeyboardEvent, callback: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    }
  };

  const renderLoadingState = () => (
    <div className="mention-item muted">Searching…</div>
  );

  const renderResults = () => (
    <>
      {results.map((u, idx) => {
        const handleUserPick = () => onPick(u);
        const handleUserHover = () => onHover(idx);
        const handleUserKeyDown = (e: React.KeyboardEvent) => handleKeyDown(e, handleUserPick);
        
        return (
          <button
            key={u.id}
            type="button"
            className={`mention-item${idx === activeIndex ? ' active' : ''}`}
            onMouseEnter={handleUserHover}
            onClick={handleUserPick}
            onKeyDown={handleUserKeyDown}
          style={{
            display: 'grid',
            gridTemplateColumns: '28px 1fr',
            gap: 8,
            alignItems: 'center',
            padding: '8px 10px',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <span
            className="mention-avatar"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: '50%',
              backgroundColor: 'var(--component-background)',
            }}
          >
            <GlyphAtom glyph={u.avatar || 'lucide:user'} fallbackText={u.name || u.email || 'User'} />
          </span>
          <div style={{ lineHeight: 1.2 }}>
            <div className="mention-primary">@{u.name || u.email || 'User'}</div>
            {u.email && <div className="mention-secondary">{u.email}</div>}
          </div>
        </button>
        );
      })}
    </>
  );

  const renderQuickEmail = () => {
    const handleEmailPick = () => onPick({ custom: true, email: query.trim(), display: query.trim() });
    const handleEmailKeyDown = (e: React.KeyboardEvent) => handleKeyDown(e, handleEmailPick);
    
    return (
      <button
        type="button"
        className="mention-item"
        onClick={handleEmailPick}
        onKeyDown={handleEmailKeyDown}
      >
        Mention <strong>@{query.trim()}</strong>
      </button>
    );
  };

  const renderEmptyState = () => (
    <div className="mention-item muted">Type a name or email…</div>
  );

  const renderContent = () => {
    if (loading) {
      return renderLoadingState();
    }
    if (results.length) {
      return renderResults();
    }
    if (showQuickEmail) {
      return renderQuickEmail();
    }
    return renderEmptyState();
  };

  return (
    <div
      className="mention-popover"
      style={{
        position: 'absolute',
        left: anchor.left,
        top: anchor.top,
      }}
      onMouseDown={(e) => e.preventDefault()}
      role="menu"
      aria-label="Mention suggestions"
      tabIndex={-1}
      id="mention-menu"
    >
      {renderContent()}
    </div>
  );
};

export default MentionPopover;
